/**
 * Machine numbers as people actually hand them over.
 *
 * `SVFID-XXXX-XXXX-XXXX`. The string is read off a terminal screen and typed
 * into this browser, or dictated down a phone line, so the parser is forgiving:
 * case is ignored, separators are dropped, and the characters people reliably
 * confuse with digits are folded the way the alphabet intends. The terminal's
 * generator never emits `I`, `L`, `O` or `U`, so folding can only rescue a
 * mistyping — it can never collide two real numbers.
 *
 * This must accept exactly what the server accepts. The authority is
 * `lic_application::machine::canonicalise` (sv_license), whose test
 * `dictation_mistakes_are_forgiven` pins these same fixtures:
 *
 *   'SVFID-7K3M-9QP2-4XZ5'   → SVFID-7K3M-9QP2-4XZ5
 *   'svfid 7k3m 9qp2 4xz5'   → SVFID-7K3M-9QP2-4XZ5
 *   'SVFID7K3M9QP24XZ5'      → SVFID-7K3M-9QP2-4XZ5
 *   '7K3M-9QP2-4XZ5'         → SVFID-7K3M-9QP2-4XZ5
 *   '  SVFID-7k3m-9qp2-4xz5' → SVFID-7K3M-9QP2-4XZ5
 *
 * (The admin app has no test runner, so those live as fixtures there and as
 * this comment here. If you add a rule, add it in both places.)
 */

/** Crockford base32 minus the characters a human confuses: no I, L, O or U. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

const FOLD: Record<string, string> = { O: '0', I: '1', L: '1', U: 'V' }

/** Prefixes we issue. Longest first, so a future prefix that starts like
 *  another cannot be half-stripped. */
const PREFIXES = ['SVDSK', 'SVCAM', 'SVNVR', 'SVFID'] as const

/** The canonical form, or null when the input is not a machine number.
 *
 *  The prefix says WHAT the device is and is therefore kept: `SVFID` is a
 *  terminal, `SVDSK` an operator's desktop, `SVCAM` a SamVision camera, `SVNVR` a video
 *  recorder (sv_license `NVR_PREFIX`). A number dictated without a prefix
 *  — which is how they travel down a phone line — is read as a terminal, the
 *  behaviour that was here before desktops existed. */
export function canonicaliseMachineNumber(input: string): string | null {
  const upper = input.trim().toUpperCase()
  const prefix = PREFIXES.find((p) => upper.startsWith(p)) ?? 'SVFID'
  const body = upper.startsWith(prefix) ? upper.slice(prefix.length) : upper

  let payload = ''
  for (const ch of body) {
    if (!/[0-9A-Z]/.test(ch)) continue
    payload += FOLD[ch] ?? ch
  }

  if (payload.length !== 12) return null
  for (const ch of payload) {
    if (!ALPHABET.includes(ch)) return null
  }
  return `${prefix}-${payload.slice(0, 4)}-${payload.slice(4, 8)}-${payload.slice(8, 12)}`
}

/**
 * True when the input looks like somebody trying to enter a machine number
 * rather than searching by name — used to decide whether to canonicalise what
 * they typed before sending it.
 */
export function looksLikeMachineNumber(input: string): boolean {
  return canonicaliseMachineNumber(input) !== null
}
